import React from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import QRCode from "react-qr-code";
import toast from "react-hot-toast";
import { Copy, ExternalLink, Pencil, Receipt } from "lucide-react";

import StatusBadge from "../../../components/StatusBadge";
import { Button } from "../../../components/ui/button";
import { Separator } from "../../../components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../components/ui/sheet";
import { APP_SINGLE_BUSINESS_URL } from "../../../constant";
import { useBankName } from "../../../hooks/use-bank-name";
import { formatLongDate } from "../../../utils/formatters";
import { formatAmount } from "../../../utils/wordFunctions";
import type { PaymentPageRow } from "./paymentColumns";

type Props = {
  page: PaymentPageRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rate: number;
  onViewTransactions: (page: PaymentPageRow) => void;
  onEdit: (page: PaymentPageRow) => void;
};

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-4 py-3">
    <span className="text-[13px] font-medium text-muted-foreground">
      {label}
    </span>
    <span className="text-right text-[13px] font-semibold text-foreground">
      {value}
    </span>
  </div>
);

const PaymentDetailSheet = ({
  page,
  open,
  onOpenChange,
  rate,
  onViewTransactions,
  onEdit,
}: Props) => {
  // Ahead of the early return — hooks can't sit behind a conditional.
  const { bankName } = useBankName();

  if (!page) return null;

  const bankLabel = page.bank
    ? [
        page.bank.accountName,
        page.bank.accountNumber,
        bankName(page.bank.bankCode),
      ]
        .filter(Boolean)
        .join(" • ")
    : null;

  const url = `${APP_SINGLE_BUSINESS_URL}/${page.reference}`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col overflow-y-auto font-manrope mds:max-w-[440px]">
        {/*
          pr-10 keeps the title clear of SheetContent's absolute close button
          (right-4 top-4), and the status badge sits under the title rather than
          beside it — it used to collide with the X.
        */}
        <SheetHeader className="pr-10 text-left">
          <SheetTitle className="font-sora text-[20px]">
            {page.name || "Untitled page"}
          </SheetTitle>
          {page.description && (
            <SheetDescription>{page.description}</SheetDescription>
          )}
          <StatusBadge status={page.status ?? "LIVE"} className="mt-2 w-fit" />
        </SheetHeader>

        <Separator className="my-4" />

        <div className="divide-y">
          <Row label="Page ID" value={`#${page.reference}`} />
          <Row label="Type" value="Payment page" />
          <Row label="Date created" value={formatLongDate(page.createdAt)} />
          <Row
            label="Amount"
            value={
              page.amount
                ? `$${rate ? formatAmount(page.amount / rate) : "—"}`
                : "Customer sets amount"
            }
          />
          <Row
            label="Naira value"
            value={page.amount ? `${formatAmount(page.amount)} NGN` : "—"}
          />
          <Row label="Payout bank" value={bankLabel || "Not set"} />
        </div>

        <Separator className="my-4" />

        <div className="flex flex-col items-center gap-3">
          <p className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
            Scan to pay
          </p>
          <div className="rounded-xl border bg-white p-4">
            <QRCode value={url} size={168} />
          </div>
          <p className="break-all text-center text-[12px] font-medium text-muted-foreground">
            {url}
          </p>
        </div>

        <div className="mt-auto grid gap-2 pt-6">
          <Button
            onClick={() => onViewTransactions(page)}
            className="h-11 w-full gap-2 rounded-xl"
          >
            <Receipt className="size-4" />
            View transactions
          </Button>

          <Button
            variant="outline"
            onClick={() => onEdit(page)}
            className="h-11 w-full gap-2 rounded-xl bg-background"
          >
            <Pencil className="size-4" />
            Edit page
          </Button>

          <div className="grid grid-cols-2 gap-2">
            <CopyToClipboard
              text={url}
              onCopy={() => toast.success("Payment link copied")}
            >
              <Button
                variant="outline"
                className="h-11 gap-2 rounded-xl bg-background"
              >
                <Copy className="size-4" />
                Copy link
              </Button>
            </CopyToClipboard>

            <Button
              variant="outline"
              onClick={() => window.open(url, "_blank")}
              className="h-11 gap-2 rounded-xl bg-background"
            >
              <ExternalLink className="size-4" />
              Visit
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PaymentDetailSheet;
